import { useEffect, useState } from "react";
import { addExpense, updateExpense } from "./TransactionService";
import { getAllAccounts } from "../accounts/AccountService";
import { getAllCategories } from "../categories/CategoryService";
import { getAllMerchants } from "../merchants/MerchantService";
import { getAllTags } from "../tags/TagService";
import "./ExpenseForm.css";
import {
  buildExpenseFormInitialValues,
  formatAccountLabel,
  readErrorMessage,
} from "./transactionUtils";

export default function ExpenseForm({
  initialValues,
  submitLabel = "Save Expense",
  title = "Add Expense",
  onSaved,
  onCancel,
  replaceTransactionId,
}) {

  const [accounts,setAccounts]=useState([]);
  const [categories,setCategories]=useState([]);
  const [merchants,setMerchants]=useState([]);
  const [tags,setTags]=useState([]);
  const [errors, setErrors] = useState({});

  const [form,setForm]=useState(buildExpenseFormInitialValues(initialValues));

  useEffect(()=>{
    load();
  },[]);

  useEffect(() => {
    setForm(buildExpenseFormInitialValues(initialValues));
    setErrors({});
  }, [initialValues]);

  const load = async ()=>{
    setAccounts(await getAllAccounts());
    setCategories(await getAllCategories());
    setMerchants(await getAllMerchants());
    setTags(await getAllTags());
  };

  const validate = (nextForm)=>{
    const nextErrors = {};
    if (!nextForm.accountId) nextErrors.accountId = "Select an account.";
    if (!nextForm.categoryId) nextErrors.categoryId = "Select a category.";
    const amount = parseFloat(nextForm.amount);
    if (!nextForm.amount || Number.isNaN(amount) || amount <= 0) nextErrors.amount = "Enter a valid amount.";
    if (!nextForm.date) nextErrors.date = "Select a date.";
    return nextErrors;
  };

  const updateField = (field, value)=>{
    setForm(f=>({ ...f, [field]: value }));
    if (errors[field]) {
      setErrors(e=>({ ...e, [field]: undefined }));
    }
  };

  const submit = async (e)=>{
    e.preventDefault();
    const nextErrors = validate(form);
    if (Object.keys(nextErrors).length > 0) {
      setErrors(nextErrors);
      return;
    }
    try {
      const res = replaceTransactionId ? await updateExpense(replaceTransactionId, form) : await addExpense(form);
      if (!res?.ok) {
        const message = await readErrorMessage(res, "Expense not saved.");
        alert(message);
        return;
      }
      alert(replaceTransactionId ? "Expense updated" : "Expense added");
      if (!replaceTransactionId) {
        setForm(buildExpenseFormInitialValues());
      }
      onSaved?.();
    } catch (err) {
      alert(err?.message || "Expense not saved.");
    }
  };

  const toggleTag = (id)=>{
    setForm(f=>({
      ...f,
      tags: f.tags.includes(id) ? f.tags.filter(t=>t!==id) : [...f.tags,id]
    }));
  };

  return (
    <form onSubmit={submit}>
      <div className="card">
  <div className="card-title">{title}</div>

  <div className="form-grid">

    <div className="transaction-field">
      <select
        className={`input ${errors.accountId ? "transaction-input-error" : ""}`}
        value={form.accountId}
        onChange={e=>updateField("accountId", e.target.value)}
        aria-invalid={Boolean(errors.accountId)}
        required
      >
        <option value="">Select Account</option>
        {accounts.map(a=><option key={a.id} value={a.id}>{formatAccountLabel(a)}</option>)}
      </select>
      {errors.accountId && <div className="transaction-error-text">{errors.accountId}</div>}
    </div>

    <div className="transaction-field">
      <select
        className={`input ${errors.categoryId ? "transaction-input-error" : ""}`}
        value={form.categoryId}
        onChange={e=>updateField("categoryId", e.target.value)}
        aria-invalid={Boolean(errors.categoryId)}
        required
      >
        <option value="">Select Category</option>
        {categories
          .filter((c) => String(c.categoryType || "").toLowerCase() !== "income")
          .map(c=><option key={c.id} value={c.id}>{c.categoryName}</option>)}
      </select>
      {errors.categoryId && <div className="transaction-error-text">{errors.categoryId}</div>}
    </div>

    <div className="transaction-field">
      <select className="input" value={form.merchantId} onChange={e=>updateField("merchantId", e.target.value)}>
        <option value="">Select Merchant</option>
        {merchants.map(m=><option key={m.id} value={m.id}>{m.merchantName}</option>)}
      </select>
    </div>

    <div className="transaction-field">
      <input
        className={`input ${errors.amount ? "transaction-input-error" : ""}`}
        type="number"
        step="0.01"
        min="0.01"
        inputMode="decimal"
        placeholder="Amount"
        value={form.amount}
        onChange={e=>updateField("amount", e.target.value)}
        aria-invalid={Boolean(errors.amount)}
        required
      />
      {errors.amount && <div className="transaction-error-text">{errors.amount}</div>}
    </div>

    <div className="transaction-field">
      <input
        className={`input ${errors.date ? "transaction-input-error" : ""}`}
        type="date"
        value={form.date}
        onChange={e=>updateField("date", e.target.value)}
        aria-invalid={Boolean(errors.date)}
        required
      />
      {errors.date && <div className="transaction-error-text">{errors.date}</div>}
    </div>

    <div className="transaction-field form-full">
      <textarea className="input" placeholder="Description"
        value={form.description}
        onChange={e=>updateField("description", e.target.value)} />
    </div>

  </div>

  {/* Tags */}
  <div className="expense-form-tags-wrap">
    {tags.map(t=>(
      <label key={t.id} className="expense-form-tag-pill">
        <input type="checkbox" checked={form.tags.includes(t.id)} onChange={()=>toggleTag(t.id)} /> {t.tagName}
      </label>
    ))}
  </div>

  <div className="expense-form-save-wrap">
    {onCancel && (
      <button type="button" className="btn" onClick={onCancel}>Cancel</button>
    )}
    <button className="btn btn-primary">{submitLabel}</button>
  </div>
</div>

    </form>
  );
}
